import React from "react";
import {
	addTimeToDate,
	Button,
	ClassNameProps,
	Column,
	compareBooleansAsc,
	compareObjects,
	count,
	dateTrunc,
	getObjectValues,
	If,
	isSameTimeOrAfter,
	Modal,
	Row,
	sort,
	StyleProps,
} from "@hex-insights/core";
import {
	AnnouncementFilterInput,
	AnnouncementOrderField,
	OrderDirection,
	truncateDisplayItems,
	useAnnouncementHomePageListQuery,
	useAreViewed,
	useDynamicTruncation,
	useInitialAreViewed,
} from "../../../../Utilities";
import { SharingRecordUtilities } from "../../../Utilities";
import { AnnouncementIcon } from "../Icon";
import { ControlledAnnouncementsListModal } from "../ListModal";

export type ViewerAnnouncementRowProps = Partial<ClassNameProps & StyleProps>;

/**
 * Displays active announcements relevant to the user (viewer). Ordered by view status (primary) and start time
 * (secondary). Offers a modal to view all announcements.
 *
 * For use on Home pages (generally).
 */
export function ViewerAnnouncementRow({ className, style }: ViewerAnnouncementRowProps) {
	// round up to the next hour but then filter out announcements that have not been released yet
	const [now] = React.useState(() => dateTrunc(addTimeToDate(new Date(), [1, "hour"]), "hour").toISOString()); // date trunc for improved caching
	const { rowFilter, modalFilter } = React.useMemo(() => {
		const viewerFilter: AnnouncementFilterInput = {
			or: [
				{
					author: [{ isCurrentEQ: true }],
				},
				{
					sharingRecord: [SharingRecordUtilities.viewerSharingRecordFilter],
				},
			],
		};

		const rowFilter: AnnouncementFilterInput = {
			and: [
				{
					startTimeLTE: now,
					or: [{ endTimeGTE: now }, { endTimeIsNull: true }],
				},
				viewerFilter,
			],
		};

		const modalFilter: AnnouncementFilterInput = {
			and: [
				{
					startTimeLTE: now,
				},
				viewerFilter,
			],
		};

		return { rowFilter, modalFilter };
	}, [now]);

	const { data } = useAnnouncementHomePageListQuery({
		variables: {
			filters: rowFilter,
			order: { field: AnnouncementOrderField.StartTime, direction: OrderDirection.Desc },
		},
	});

	const ids = React.useMemo(() => data?.announcementConnection.edges.map((e) => e.node.id) ?? [], [data]);
	const viewMap = useAreViewed(ids);
	const numNew = count(getObjectValues(viewMap), (viewed) => !viewed);
	const initialViewMap = useInitialAreViewed(ids);

	const sortedEdges = React.useMemo(() => {
		return sort(
			data?.announcementConnection.edges.filter((e) => isSameTimeOrAfter(new Date(), e.node.startTime)) ?? [],
			compareObjects((e) => initialViewMap[e.node.id], compareBooleansAsc()),
		);
	}, [data, initialViewMap]);

	const ref = React.useRef<HTMLDivElement | null>(null);
	const gapSize = 32;
	const { maxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize, syncID: sortedEdges.length > 0 },
	);
	const { displayedItems: displayedAnnouncementEdges } = truncateDisplayItems(sortedEdges, maxDisplayed, {
		includeLast: true,
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Column justify="spaced-start" align="flex-end" verticalSpacing="0.5rem" className={className} style={style}>
			<div ref={ref} style={{ width: "100%" }}>
				<Row justify="spaced-start" horizontalSpacing={`${gapSize}px`} style={{ width: "100%" }}>
					{displayedAnnouncementEdges.map((e) => (
						<AnnouncementIcon key={e.node.id} announcement={e.node} isNew={!viewMap[e.node.id]} />
					))}
				</Row>
			</div>

			<If condition={displayedAnnouncementEdges.length > 0}>
				<div>
					<Button variant="link" size="small" onClick={openModal}>
						View All <If condition={!!maxDisplayed && maxDisplayed >= 3}>Announcements</If>{" "}
						<If condition={numNew > 0}>({numNew} New)</If>
					</Button>
				</div>
			</If>

			<Modal.If condition={isModalOpen}>
				<ControlledAnnouncementsListModal filters={modalFilter} onClose={closeModal} />
			</Modal.If>
		</Column>
	);
}
